import * as React from 'react';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import type { Employee } from '@/types';

interface EmployeeComboboxProps {
  employees: Employee[];
  value?: string;
  onValueChange: (value: string, employee: Employee | undefined) => void;
  placeholder?: string;
  recentEmployeeIds?: string[];
  maxRecentEmployees?: number;
}

export function EmployeeCombobox({
  employees,
  value,
  onValueChange,
  placeholder = 'Select a project manager...',
  recentEmployeeIds = [],
  maxRecentEmployees = 5,
}: EmployeeComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');

  // Get recent employees
  const recentEmployees = React.useMemo(() => {
    return recentEmployeeIds
      .map((id) => employees.find((e) => String(e.id) === id))
      .filter((e): e is Employee => e !== undefined)
      .slice(0, maxRecentEmployees);
  }, [employees, recentEmployeeIds, maxRecentEmployees]);

  // Get selected employee
  const selectedEmployee = employees.find((employee) => String(employee.id) === value);

  // Filter employees for modal table based on search
  const filteredEmployees = React.useMemo(() => {
    if (!searchQuery.trim()) return employees;
    
    const query = searchQuery.toLowerCase();
    return employees.filter(
      (employee) =>
        employee.name.toLowerCase().includes(query) ||
        employee.role?.toLowerCase().includes(query) ||
        employee.email?.toLowerCase().includes(query) ||
        employee.department?.toLowerCase().includes(query)
    );
  }, [employees, searchQuery]);

  const handleSelect = (employee: Employee) => {
    onValueChange(String(employee.id), employee);
    setOpen(false);
    setModalOpen(false);
  };

  const handleShowMore = () => {
    setOpen(false);
    setModalOpen(true);
    setSearchQuery('');
  };

  return (
    <>
      {/* Main Combobox - Shows Recent Employees */}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
          >
            {selectedEmployee ? selectedEmployee.name : placeholder}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[400px] p-0" align="start">
          <Command>
            <CommandInput placeholder="Search recent employees..." />
            <CommandList>
              <CommandEmpty>No recent employees found.</CommandEmpty>
              
              {/* Recent Employees */}
              {recentEmployees.length > 0 && (
                <CommandGroup heading="Recent Employees">
                  {recentEmployees.map((employee) => (
                    <CommandItem
                      key={employee.id}
                      value={employee.name}
                      onSelect={() => handleSelect(employee)}
                      className="cursor-pointer"
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          value === String(employee.id) ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                      <div className="flex flex-col">
                        <span className="font-medium">{employee.name}</span>
                        <span className="text-xs text-muted-foreground">
                          {employee.role}
                          {employee.department && ` - ${employee.department}`}
                        </span>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              {/* Show More Button */}
              <CommandGroup>
                <CommandItem
                  onSelect={handleShowMore}
                  className="cursor-pointer justify-center font-medium text-primary"
                >
                  <ChevronsUpDown className="mr-2 h-4 w-4" />
                  Show More ({employees.length - recentEmployees.length} more employees)
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Modal Dialog - Shows All Employees in Table */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Select Project Manager</DialogTitle>
            <DialogDescription>
              Choose a project manager from the list below. Click on a row to select.
            </DialogDescription>
          </DialogHeader>

          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by name, role, email, department..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Table Container with Scroll */}
          <div className="flex-1 overflow-auto border rounded-md">
            <Table>
              <TableHeader className="sticky top-0 bg-background z-10">
                <TableRow>
                  <TableHead className="w-[40px]"></TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Email</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEmployees.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                      No employees found matching your search.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredEmployees.map((employee) => (
                    <TableRow
                      key={employee.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleSelect(employee)}
                      data-state={value === String(employee.id) ? 'selected' : undefined}
                    >
                      <TableCell>
                        <Check
                          className={cn(
                            'h-4 w-4',
                            value === String(employee.id) ? 'opacity-100' : 'opacity-0'
                          )}
                        />
                      </TableCell>
                      <TableCell className="font-medium">{employee.name}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {employee.role || '-'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {employee.department || '-'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {employee.email || '-'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Results Count */}
          <div className="text-sm text-muted-foreground">
            Showing {filteredEmployees.length} of {employees.length} employees
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
